import React, { useState, useEffect } from 'react';
import { fetchEditorialBoard } from '../utils/api';
import '../styles/About.css';

const EditorialBoard = () => {
  const [board, setBoard] = useState({
    editorInChief: [],
    associateEditors: [],
    editorialBoard: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadEditorialBoard = async () => {
      try {
        setLoading(true);
        const data = await fetchEditorialBoard();
        // Ensure data has the expected structure
        setBoard({
          editorInChief: Array.isArray(data?.editorInChief) ? data.editorInChief : [],
          associateEditors: Array.isArray(data?.associateEditors) ? data.associateEditors : [],
          editorialBoard: Array.isArray(data?.editorialBoard) ? data.editorialBoard : []
        });
      } catch (err) {
        console.error('Error loading editorial board:', err);
        setError('Failed to load editorial board information. Please try again later.');
        // Set default empty arrays on error
        setBoard({
          editorInChief: [],
          associateEditors: [],
          editorialBoard: []
        });
      } finally {
        setLoading(false);
      }
    };

    loadEditorialBoard();
  }, []);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading editorial board...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const renderEditorCard = (editor) => (
    <div className="board-member-card">
      <div className="board-member-header">
        <div>
          <h3 className="board-member-name">{editor.name}</h3>
          <p className="text-muted">{editor.affiliation}</p>
        </div>
        {editor.role && (
          <span className="board-member-badge">{editor.role}</span>
        )}
      </div>
      <div className="board-member-details">
        {editor.expertise && (
          <div className="board-member-expertise">
            {editor.expertise.split(',').map((exp, idx) => (
              <span key={idx} className="expertise-tag">{exp.trim()}</span>
            ))}
          </div>
        )}
        {editor.email && (
          <div className="mt-3">
            <a href={`mailto:${editor.email}`} className="contact-item" style={{ textDecoration: 'none' }}>
              <i className="fas fa-envelope"></i>
              {editor.email}
            </a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Hero Section */}
      <div className="about-hero">
        <div className="container">
          <h1 className="about-hero-title">
            <i className="fas fa-users-cog me-3"></i>
            Editorial Board
          </h1>
          <p className="about-hero-subtitle">
            Distinguished experts guiding the journal's academic excellence
          </p>
        </div>
      </div>

      <div className="container py-5">
        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">
            <div className="about-card" style={{ animationDelay: '0.1s' }}>
              <p className="lead text-center">
                The Federal University Wukari Journal of Agriculture and Life Sciences is supported by a distinguished
                editorial board of experts in various fields of agriculture and life sciences.
              </p>
            </div>
          </div>
        </div>

        {/* Editor-in-Chief */}
        <section className="editorial-board-section">
          <div className="about-card" style={{ animationDelay: '0.2s' }}>
            <div className="about-card-header">
              <h2 className="about-card-title">
                <i className="fas fa-crown"></i>
                Editor-in-Chief
              </h2>
            </div>
            <div className="row">
              {board.editorInChief.map((editor, index) => (
                <div key={index} className="col-lg-8 mx-auto">
                  {renderEditorCard(editor)}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Associate Editors */}
        <section className="editorial-board-section">
          <div className="about-card" style={{ animationDelay: '0.3s' }}>
            <div className="about-card-header">
              <h2 className="about-card-title">
                <i className="fas fa-user-tie"></i>
                Associate Editors
              </h2>
            </div>
            <div className="row">
              {board.associateEditors.map((editor, index) => (
                <div key={index} className="col-lg-6">
                  {renderEditorCard(editor)}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Editorial Board Members */}
        <section className="editorial-board-section">
          <div className="about-card" style={{ animationDelay: '0.4s' }}>
            <div className="about-card-header">
              <h2 className="about-card-title">
                <i className="fas fa-users"></i>
                Editorial Board Members
              </h2>
            </div>
            <div className="row">
              {board.editorialBoard.map((editor, index) => (
                <div key={index} className="col-lg-6">
                  {renderEditorCard(editor)}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Editorial Policies */}
        <section className="editorial-board-section">
          <div className="about-card" style={{ animationDelay: '0.5s' }}>
            <div className="about-card-header">
              <h2 className="about-card-title">
                <i className="fas fa-file-contract"></i>
                Editorial Policies
              </h2>
            </div>
            <h5>Peer Review Process</h5>
            <p>
              All manuscripts submitted to FUWJALS undergo a thorough double-blind peer review process. 
              Initial screening is conducted by the Editor-in-Chief to assess suitability for the journal. 
              Suitable manuscripts are then assigned to relevant Section Editors who oversee the peer review process.
            </p>
            <p>
              Each manuscript is reviewed by at least two independent experts in the field who evaluate the 
              scientific merit, originality, methodology, and relevance of the research. Authors are provided 
              with detailed feedback and recommendations for improvement.
            </p>
            
            <h5 className="mt-4">Publication Ethics</h5>
            <p>
              FUWJALS adheres to the highest standards of publication ethics and expects the same from authors, 
              reviewers, and editors. The journal follows the guidelines of the Committee on Publication Ethics (COPE).
            </p>
            <ul>
              <li>
                <strong>Originality and Plagiarism:</strong> All submitted manuscripts must be original and not 
                under consideration elsewhere. Plagiarism in any form is unacceptable.
              </li>
              <li>
                <strong>Authorship:</strong> All listed authors must have made significant contributions to the 
                research and approve the final version of the manuscript.
              </li>
              <li>
                <strong>Conflict of Interest:</strong> Authors, reviewers, and editors must disclose any potential 
                conflicts of interest.
              </li>
              <li>
                <strong>Research Ethics:</strong> Research involving humans or animals must adhere to relevant 
                ethical guidelines and obtain appropriate approvals.
              </li>
            </ul>
          </div>
        </section>
      </div>
    </>
  );
};

export default EditorialBoard;
